import React, { useEffect, useState } from "react";
import { Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, Input, HStack, Button, IconButton, useDisclosure, useToast } from "@chakra-ui/react";
import { FiTrash2 } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { saApi } from "../../api/superAdminApi";
import { useNavigate } from "react-router-dom";
import CreateCompanyModal from "../../components/Modal/CreateCompanyModal";
import ConfirmDialog from "../../components/Portal/ConfirmDialog";

const Configuration = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await saApi.listCompanies({ search });
      setItems(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompanyCreated = () => {
    load(); // Refresh the companies list
  };

  const askDelete = (e, company) => {
    e.stopPropagation(); // row click navigates; don't follow it
    setDeleteTarget(company);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await saApi.deleteCompany(deleteTarget.id);
      toast({ title: t("superAdmin.deleteCompany.success"), status: "success" });
      // Refresh on the next tick so the dialog finishes closing (and returns
      // focus to its trigger row) before that row is removed. Mutating the
      // list in the same commit triggers a React removeChild crash.
      setTimeout(() => {
        setDeleteTarget(null);
        load();
      }, 0);
    } catch (err) {
      toast({ title: t("superAdmin.deleteCompany.error"), status: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="md">Configuration</Heading>
        <HStack>
          <Input placeholder="Search name/code" value={search} onChange={(e) => setSearch(e.target.value)} size="sm" w="240px" />
          <Button size="sm" onClick={load}>Search</Button>
          <Button colorScheme="blue" size="sm" onClick={onOpen}>
            Create Company
          </Button>
        </HStack>
      </HStack>
      
      {loading ? (
        <Spinner />
      ) : error ? (
        <Box color="red.500">{error}</Box>
      ) : (
        <Table variant="simple" bg="white" borderWidth="1px" borderRadius="md">
          <Thead>
            <Tr>
              <Th>Company ID</Th>
              <Th>Name</Th>
              <Th>Code</Th>
              <Th>Active</Th>
              <Th>Trial start</Th>
              <Th>Trial end</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {items.map((c) => (
              <Tr key={c.id} _hover={{ bg: "gray.50", cursor: "pointer" }} onClick={() => navigate(`/admin/companies/${c.id}`)}>
                <Td>{c.id}</Td>
                <Td>{c.company_name}</Td>
                <Td>{c.company_code}</Td>
                <Td>{String(c.is_active)}</Td>
                <Td>{c.trial_start_at ? new Date(c.trial_start_at).toLocaleString() : ""}</Td>
                <Td>{c.trial_end_at ? new Date(c.trial_end_at).toLocaleString() : ""}</Td>
                <Td textAlign="right">
                  <IconButton
                    aria-label={t("superAdmin.deleteCompany.tooltip")}
                    title={t("superAdmin.deleteCompany.tooltip")}
                    icon={<FiTrash2 />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={(e) => askDelete(e, c)}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <CreateCompanyModal
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={handleCompanyCreated}
      />

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDelete}
        title={t("superAdmin.deleteCompany.title")}
        body={t("superAdmin.deleteCompany.body", { name: deleteTarget?.company_name || deleteTarget?.company_code || "" })}
        confirmLabel={t("superAdmin.deleteCompany.confirm")}
        isLoading={deleting}
      />
    </Box>
  );
};

export default Configuration;


